/**
 * Stale-chunk recovery (issue #530).
 *
 * After a deploy, a client holding an old index.html may request a lazily-loaded
 * chunk whose hashed filename no longer exists. Vite fires `vite:preloadError`
 * when a dynamic-import preload fails. We reload once to pull the fresh index +
 * current chunks. A debounce stops a genuinely-missing chunk from causing a
 * reload loop (after which the app's error boundary handles it instead).
 */

const RELOAD_KEY = "vite:preloadError:lastReload"
const RELOAD_DEBOUNCE_MS = 10_000

/**
 * Handle a `vite:preloadError`. Returns true if it triggered a reload. Pure
 * except for the reload + sessionStorage side effects; `now` is injectable for
 * tests.
 */
export const handlePreloadError = (event: Event, now: number = Date.now()): boolean => {
  let lastReload = 0
  try {
    lastReload = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0)
  } catch {
    // sessionStorage can be unavailable (private mode); proceed best-effort.
  }
  if (!Number.isFinite(lastReload)) {
    lastReload = 0 // corrupt/non-numeric stored value -> treat as never reloaded
  }

  // Already reloaded very recently -> the chunk is genuinely gone, not just a
  // stale cache. Stop reloading and let the error surface to the boundary.
  // (lastReload === 0 means we've never reloaded -> always recover.)
  if (lastReload > 0 && now - lastReload < RELOAD_DEBOUNCE_MS) {
    return false
  }

  try {
    sessionStorage.setItem(RELOAD_KEY, String(now))
  } catch {
    // ignore storage failures
  }

  // Prevent Vite from also throwing the unhandled preload error.
  event.preventDefault()
  window.location.reload()
  return true
}

/** Register the stale-chunk recovery listener. Call once at app startup. */
export const registerChunkReload = (): void => {
  window.addEventListener("vite:preloadError", event => {
    handlePreloadError(event)
  })
}
